/**
 * "Is this reportable?" RIDDOR checker (free tool, /free/riddor-reportable-checker;
 * SEO plan Workstream D row 10 and Workstream E row 5).
 *
 * Pure logic, no React: a visitor's answers in, the questions still to ask and
 * the fixed blocks of the outcome out. Every word of every question and
 * outcome lives here, so the page, the tests and the safety review all read
 * one set of text. The decision logic follows RIDDOR 2013 (SI 2013/1471) as
 * read on legislation.gov.uk on 31 Oct 2026, and HSE's RIDDOR pages:
 *
 *  - reg 2      "work-related accident", "road", "consecutive days", "routine
 *               work", "diagnosis"
 *  - reg 3(1)   the responsible person: an employee's employer; for a person not
 *               at work, a self-employed person or any other dangerous
 *               occurrence, the person in control of the premises; for a
 *               disease, the employer or the relevant self-employed person
 *  - reg 4(1)   specified injuries to workers; reg 4(2) over-7-day incapacitation
 *               (report within 15 days)
 *  - reg 5      non-workers taken from the scene to hospital for treatment
 *               (5(a)), or suffering a specified injury on hospital premises
 *               (5(b))
 *  - reg 6      deaths (6(3) employee dies within a year; 6(4) not a
 *               self-employed person on premises they control)
 *  - reg 7      dangerous occurrences (Schedule 2). The checker lists only the
 *               construction-relevant ones, so "None of these" is never a
 *               confident "not reportable": it sends the visitor to the full
 *               Schedule 2 list
 *  - regs 8, 9  occupational diseases; cancers and biological agents
 *  - reg 12     records, including over-3-day injuries (12(1)(c))
 *  - reg 14(3)  road traffic exemption and its exceptions
 *  - Sch 1      notify without delay and report within 10 days (para 1(1));
 *               no immediate notification for a self-employed person injured
 *               at premises they own or occupy (para 1(2)); diseases without
 *               delay
 *  - Social Security (Claims and Payments) Regulations 1979 reg 25(3): the
 *               accident book for employers of 10 or more
 *
 * Gas incidents (reg 11) are out of scope: they are reported by gas
 * conveyors, suppliers and Gas Safe engineers, not by the site.
 *
 * Pinned path by path in __tests__/lib/marketing/riddor-checker/checker.test.ts.
 */

import {
  HSE_DANGEROUS_OCCURRENCES_URL,
  HSE_HOW_TO_REPORT_URL,
  HSE_OCCUPATIONAL_DISEASES_URL,
  HSE_REPORTABLE_INCIDENTS_URL,
  HSE_WHO_SHOULD_REPORT_URL,
  HSE_WORK_RELATED_DEATHS_URL,
  PRESCRIBED_UNDERTAKINGS_URL,
  SS_CLAIMS_REG_25_URL,
  riddorRegulationUrl,
  riddorScheduleUrl,
} from "./sources";

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

export type WhatHappened = "death" | "injury" | "disease" | "occurrence";
export type YesNoUnsure = "yes" | "no" | "unsure";
export type RoadAnswer = "no" | "exception" | "ordinary";
export type Person = "employee" | "other-employee" | "me" | "self-employed" | "public";
export type DaysAnswer = "over7" | "over3" | "three-or-fewer" | "unsure";
export type HospitalAnswer = "hospital-premises" | "treated" | "checks-only" | "no" | "unsure";
export type DiagnosisPerson = "employee" | "me" | "other-employee" | "self-employed";
/** "them" is the self-employed person who died or was hurt, offered only when that person is someone else. */
export type ControlAnswer = "me" | "them" | "other" | "unsure";

export const REG_8_DISEASES = ["cts", "cramp", "dermatitis", "havs", "asthma", "tendonitis"] as const;
export type Reg8Disease = (typeof REG_8_DISEASES)[number];
export type DiseaseAnswer = Reg8Disease | "cancer-bio" | "none" | "unsure";

export const LISTED_OCCURRENCES = [
  "lifting",
  "pressure",
  "overhead-line",
  "electrical-fire",
  "breathing-apparatus",
  "scaffold",
  "structure",
  "falsework",
  "fire",
  "flammable-release",
  "substance-escape",
] as const;
export type ListedOccurrence = (typeof LISTED_OCCURRENCES)[number];
export type OccurrenceAnswer = ListedOccurrence | "none" | "unsure";

export interface RiddorAnswers {
  what?: WhatHappened;
  workRelated?: YesNoUnsure;
  road?: RoadAnswer;
  person?: Person;
  specified?: YesNoUnsure;
  days?: DaysAnswer;
  hospital?: HospitalAnswer;
  disease?: DiseaseAnswer;
  diagnosisPerson?: DiagnosisPerson;
  occurrence?: OccurrenceAnswer;
  control?: ControlAnswer;
}

export type QuestionId = keyof RiddorAnswers;

export interface QuestionOption {
  value: string;
  label: string;
}

export interface Question {
  id: QuestionId;
  label: string;
  hint?: string;
  /** A bulleted list printed under the hint (the specified injuries). */
  list?: readonly string[];
  options: readonly QuestionOption[];
}

// ---------------------------------------------------------------------------
// Questions (exact wording shown to the visitor)
// ---------------------------------------------------------------------------

export const Q_WHAT: Question = {
  id: "what",
  label: "What happened?",
  hint: "If someone was hurt and something dangerous also happened, such as a scaffold collapse, check the injury first, then start again for the dangerous occurrence.",
  options: [
    { value: "death", label: "Someone died" },
    { value: "injury", label: "Someone was injured" },
    { value: "disease", label: "A doctor diagnosed a work-related illness or condition" },
    { value: "occurrence", label: "Nobody was hurt, but something dangerous happened (a near miss)" },
  ],
};

export const Q_WORK_RELATED: Question = {
  id: "workRelated",
  label: "Was it connected with work?",
  hint: "RIDDOR covers accidents arising out of or in connection with work. That includes accidents caused by how the work was done, the plant or substances used, or the condition of the site or premises, and physical violence to someone at work.",
  options: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No, it had nothing to do with work" },
    { value: "unsure", label: "Not sure" },
  ],
};

export const Q_ROAD: Question = {
  id: "road",
  label: "Did it involve a vehicle moving on a public road?",
  hint: "A public road is any road the public has access to. A private site road the public cannot use is not a public road.",
  options: [
    { value: "no", label: "No" },
    {
      value: "exception",
      label:
        "Yes, but the person was loading or unloading the vehicle, or working on or alongside the road (for example roadworks, or work on a building next to the road), or was hurt by someone doing that work, or by a substance the vehicle was carrying, or a train was involved",
    },
    { value: "ordinary", label: "Yes, and none of those apply (for example a collision while driving to a job)" },
  ],
};

const PERSON_OPTIONS: Readonly<Record<Person, string>> = {
  employee: "Someone I employ",
  "other-employee": "Someone employed by another firm, for example a subcontractor's employee",
  me: "Me. I am self-employed",
  "self-employed": "A self-employed worker who is not my employee, for example a self-employed subcontractor",
  public: "Someone not at work, for example a member of the public, a visitor, the client or a child",
};

/** "Who died?" has no "Me" option; "Who was hurt?" has all five. */
export function personQuestion(what: "death" | "injury"): Question {
  const people: Person[] =
    what === "death" ? ["employee", "other-employee", "self-employed", "public"] : ["employee", "other-employee", "me", "self-employed", "public"];
  return {
    id: "person",
    label: what === "death" ? "Who died?" : "Who was hurt?",
    options: people.map((value) => ({ value, label: PERSON_OPTIONS[value] })),
  };
}

export const SPECIFIED_INJURIES: readonly string[] = [
  "a bone fracture diagnosed by a doctor, other than to a finger, thumb or toe",
  "amputation of an arm, hand, finger, thumb, leg, foot or toe",
  "an injury a doctor says is likely to cause permanent blinding or reduced sight in one or both eyes",
  "a crush injury to the head or torso causing damage to the brain or to internal organs in the chest or abdomen",
  "a burn or scald covering more than 10% of the body, or causing significant damage to the eyes, respiratory system or other vital organs",
  "any degree of scalping needing hospital treatment",
  "loss of consciousness caused by a head injury or asphyxia",
  "any other injury from working in an enclosed space that leads to hypothermia or heat-induced illness, or needs resuscitation or admission to hospital for more than 24 hours",
];

export const Q_SPECIFIED: Question = {
  id: "specified",
  label: "Was it one of these injuries?",
  hint: "These are the specified injuries in RIDDOR regulation 4(1).",
  list: SPECIFIED_INJURIES,
  options: [
    { value: "yes", label: "Yes, one or more of these" },
    { value: "no", label: "No, none of these" },
    { value: "unsure", label: "Not sure yet, for example still waiting for a doctor's diagnosis" },
  ],
};

export const Q_DAYS: Question = {
  id: "days",
  label: "How long will they be unable to do their normal work because of the injury?",
  hint: "Do not count the day of the accident. Do count weekends, rest days and any other days they would not have worked. Normal work means the work they could reasonably be expected to do.",
  options: [
    { value: "over7", label: "More than 7 days in a row" },
    { value: "over3", label: "More than 3 days in a row, but not more than 7" },
    { value: "three-or-fewer", label: "3 days or fewer" },
    { value: "unsure", label: "Too early to tell" },
  ],
};

export const Q_HOSPITAL: Question = {
  id: "hospital",
  label: "Were they taken from the scene of the accident to hospital for treatment of the injury?",
  hint: "HSE says examinations and tests, such as X-rays, are not treatment. Treatment includes a dressing, stitches, a plaster cast or surgery.",
  options: [
    { value: "hospital-premises", label: "The accident happened on hospital premises, for example during building work at a hospital" },
    { value: "treated", label: "Yes, they were taken from the scene to hospital and treated for the injury" },
    { value: "checks-only", label: "They went to hospital only for checks or tests, or as a precaution with no injury apparent" },
    { value: "no", label: "No, they were not taken to hospital from the scene" },
    { value: "unsure", label: "Not sure" },
  ],
};

export const DISEASE_LABELS: Readonly<Record<Reg8Disease | "cancer-bio", string>> = {
  cts: "Carpal tunnel syndrome, and the person's work involves regular use of percussive or vibrating tools",
  cramp: "Cramp in the hand or forearm, and the person's work involves long periods of repetitive movement of the fingers, hand or arm",
  dermatitis: "Occupational dermatitis, and the person's work involves significant or regular exposure to a known skin sensitiser or irritant",
  havs: "Hand arm vibration syndrome (HAVS), and the person's work involves regular use of percussive or vibrating tools, or holding materials being worked on by percussive or vibrating processes",
  asthma: "Occupational asthma, and the person's work involves significant or regular exposure to a known respiratory sensitiser",
  tendonitis: "Tendonitis or tenosynovitis in the hand or forearm, and the person's work is physically demanding and involves frequent, repetitive movements",
  "cancer-bio":
    "A cancer the doctor links to exposure at work to a known carcinogen or mutagen (for example asbestos), or a disease the doctor links to exposure at work to a biological agent",
};

export const Q_DISEASE: Question = {
  id: "disease",
  label: "What did the doctor diagnose?",
  hint: "RIDDOR counts a doctor's diagnosis of new symptoms, or of symptoms that have got significantly worse. For an employee, the diagnosis must be in writing.",
  options: [
    ...[...REG_8_DISEASES, "cancer-bio" as const].map((value) => ({ value, label: DISEASE_LABELS[value] })),
    { value: "none", label: "None of these" },
    { value: "unsure", label: "Not sure" },
  ],
};

export const Q_DIAGNOSIS_PERSON: Question = {
  id: "diagnosisPerson",
  label: "Whose diagnosis is it?",
  options: [
    { value: "employee", label: "Someone I employ" },
    { value: "me", label: "Mine. I am self-employed" },
    { value: "other-employee", label: "Someone employed by another firm" },
    { value: "self-employed", label: "A self-employed worker who is not my employee" },
  ],
};

export const OCCURRENCE_LABELS: Readonly<Record<ListedOccurrence, { label: string; paragraph: number }>> = {
  lifting: {
    label: "Lifting equipment, such as a crane or a hoist, collapsed or overturned, or a load-bearing part of it failed (not just a lifting accessory such as a sling)",
    paragraph: 1,
  },
  pressure: {
    label: "A closed vessel or its pipework in a pressure system failed, and the failure could have killed someone",
    paragraph: 2,
  },
  "overhead-line": {
    label: "Plant or equipment touched an uninsulated overhead electric line of more than 200 volts, or came close enough to one to cause an electrical discharge",
    paragraph: 3,
  },
  "electrical-fire": {
    label: "An electrical short circuit or overload caused a fire or explosion that stopped the plant for more than 24 hours, or caused a significant risk of death",
    paragraph: 4,
  },
  "breathing-apparatus": {
    label: "Breathing apparatus malfunctioned in a way that caused a significant risk of injury to the user, or would have done if it had happened in use",
    paragraph: 12,
  },
  scaffold: {
    label:
      "A scaffold collapsed or partly collapsed, including falling, buckling or overturning: a substantial part of a scaffold more than 5 metres high, a supporting part of a slung or suspended scaffold so that a working platform fell, or any part of a scaffold where someone falling would risk drowning",
    paragraph: 18,
  },
  structure: {
    label:
      "During construction work (including demolition, refurbishment or maintenance), a structure collapsed or partly collapsed unintentionally with a fall of more than 5 tonnes of material, or a floor or wall of a place of work collapsed",
    paragraph: 23,
  },
  falsework: { label: "Falsework collapsed or partly collapsed unintentionally", paragraph: 24 },
  fire: {
    label: "An unintentional explosion or fire stopped the plant, or normal work in the premises, for more than 24 hours",
    paragraph: 25,
  },
  "flammable-release": {
    label:
      "A sudden, unintentional and uncontrolled release of a flammable liquid or gas: inside a building, 100 kilograms or more of a flammable liquid, 10 kilograms or more of a flammable liquid above its normal boiling point, or 10 kilograms or more of a flammable gas; in the open air, 500 kilograms or more of a flammable liquid or gas",
    paragraph: 26,
  },
  "substance-escape": {
    label: "An unintentional release or escape of any substance that could injure someone, other than through flammable liquids or gases burning",
    paragraph: 27,
  },
};

export const Q_OCCURRENCE: Question = {
  id: "occurrence",
  label: "Which of these happened?",
  hint: "These are the dangerous occurrences in Schedule 2 of RIDDOR most likely on a building site. It is not the full list. They are reportable even if nobody was hurt.",
  options: [
    ...LISTED_OCCURRENCES.map((value) => ({
      value,
      label: `${OCCURRENCE_LABELS[value].label} (Schedule 2, paragraph ${OCCURRENCE_LABELS[value].paragraph})`,
    })),
    { value: "none", label: "None of these" },
    { value: "unsure", label: "Something like one of these, but I am not sure it fits" },
  ],
};

const CONTROL_ME: QuestionOption = {
  value: "me",
  label: "Me or my business, for example my own premises or yard, or a job I am running, including work in someone's home",
};
const CONTROL_OTHER: QuestionOption = {
  value: "other",
  label: "Another business, for example the principal contractor, the main contractor or the client's business",
};
const CONTROL_UNSURE: QuestionOption = { value: "unsure", label: "Not sure" };

export const Q_CONTROL: Question = {
  id: "control",
  label: "Who was in control of the site or premises where it happened?",
  hint: "RIDDOR means the person or business whose work gave them control of the premises at the time.",
  options: [CONTROL_ME, CONTROL_OTHER, CONTROL_UNSURE],
};

/**
 * The control question for a self-employed person who is not the visitor. It
 * adds the person themselves, because the answer changes the outcome: RIDDOR
 * reg 6(4) takes a self-employed person's fatal accident on premises they
 * controlled out of the duty to report, and for an injury they are then the
 * responsible person themselves (reg 3(1)(a)(ii)).
 */
export function controlQuestionForSelfEmployed(what: "death" | "injury"): Question {
  return {
    ...Q_CONTROL,
    options: [
      CONTROL_ME,
      {
        value: "them",
        label:
          what === "death"
            ? "The self-employed person who died, for example their own premises or yard, or a job they were running"
            : "The self-employed worker who was hurt, for example their own premises or yard, or a job they were running",
      },
      CONTROL_OTHER,
      CONTROL_UNSURE,
    ],
  };
}

// ---------------------------------------------------------------------------
// Outcome blocks (exact wording of every outcome)
// ---------------------------------------------------------------------------

export interface OutcomeLine {
  text: string;
  /** The citation as printed, e.g. "RIDDOR reg 4(1)". Absent for a practical tip. */
  cite?: string;
  /** legislation.gov.uk or hse.gov.uk page the citation links to. */
  url?: string;
}

export interface Block {
  id: BlockId;
  heading: string;
  lines: readonly OutcomeLine[];
}

export type BlockId =
  // Reportable
  | "death"
  | "specified"
  | "over7"
  | "public-hospital"
  | "disease"
  | "cancer-bio"
  | "occurrence"
  // Who reports
  | "who-employer"
  | "who-other-employer"
  | "who-you-control"
  | "who-self-own"
  | "who-self-other"
  | "who-self-themselves"
  | "who-other-control"
  | "who-control-unsure"
  | "who-disease-employer"
  | "who-disease-self"
  | "who-disease-other-employer"
  | "who-disease-other-self"
  // How and when
  | "how-notify-phone"
  | "how-notify-online"
  | "how-notify-self-own"
  | "how-over7"
  | "how-disease"
  | "how-cancer-bio"
  | "keep-record"
  // Not reportable
  | "not-work-related"
  | "not-road"
  | "not-over3"
  | "not-minor"
  | "not-public"
  | "not-disease"
  | "not-death-self-control"
  | "accident-book"
  // Get advice
  | "advice-work"
  | "advice-specified"
  | "advice-days"
  | "advice-hospital"
  | "advice-disease"
  | "advice-hospital-premises"
  | "advice-occurrence"
  | "advice-occurrence-unlisted";

const r = (text: string, regulation: number, cite: string): OutcomeLine => ({
  text,
  cite: `RIDDOR ${cite}`,
  url: riddorRegulationUrl(regulation),
});
const s = (text: string, schedule: 1 | 2, cite: string): OutcomeLine => ({
  text,
  cite: `RIDDOR ${cite}`,
  url: riddorScheduleUrl(schedule),
});
const h = (text: string, url: string): OutcomeLine => ({ text, cite: "HSE", url });
const tip = (text: string): OutcomeLine => ({ text });

const TEN_DAYS = s("Then send HSE a report within 10 days of the incident.", 1, "Sch 1 para 1(1)(b)");
const PHONE_LINE = h(
  "Report online using HSE's RIDDOR forms. For a death, or a specified injury to a worker, you can phone HSE instead on 0345 300 9923.",
  HSE_HOW_TO_REPORT_URL,
);

export const BLOCKS: Readonly<Record<BlockId, Block>> = {
  // ---- Reportable -----------------------------------------------------------
  death: {
    id: "death",
    heading: "A work-related death is reportable",
    lines: [
      r("Where any person dies as a result of a work-related accident, it must be reported.", 6, "reg 6(1)"),
      r("This does not apply to a self-employed person who has a fatal accident on premises they control themselves.", 6, "reg 6(4)"),
      r("If an employee dies within one year of an accident that caused a reportable injury, the employer must also tell HSE about the death without delay.", 6, "reg 6(3)"),
    ],
  },
  specified: {
    id: "specified",
    heading: "A specified injury to a worker is reportable",
    lines: [r("A specified injury to a person at work, caused by a work-related accident, must be reported.", 4, "reg 4(1)")],
  },
  over7: {
    id: "over7",
    heading: "An over-7-day injury is reportable",
    lines: [
      r(
        "An injury that stops a person at work doing their normal work for more than 7 days in a row, not counting the day of the accident, must be reported.",
        4,
        "reg 4(2)",
      ),
    ],
  },
  "public-hospital": {
    id: "public-hospital",
    heading: "An injury to someone not at work who was taken to hospital is reportable",
    lines: [
      r(
        "Where someone not at work is injured in a work-related accident and is taken from the scene to hospital for treatment of that injury, it must be reported.",
        5,
        "reg 5(a)",
      ),
    ],
  },
  disease: {
    id: "disease",
    heading: "This occupational disease is reportable",
    lines: [
      r(
        "When the responsible person receives a doctor's diagnosis of this condition, for a person whose work involves the activity linked to it, it must be reported.",
        8,
        "reg 8",
      ),
    ],
  },
  "cancer-bio": {
    id: "cancer-bio",
    heading: "This diagnosis is reportable",
    lines: [
      r(
        "A cancer attributed to exposure at work to a known human carcinogen or mutagen, or a disease attributed to exposure at work to a biological agent, must be reported once the responsible person receives the diagnosis.",
        9,
        "reg 9",
      ),
    ],
  },
  occurrence: {
    id: "occurrence",
    heading: "This dangerous occurrence is reportable",
    lines: [
      r("A dangerous occurrence of a class listed in Schedule 2 must be reported, even if nobody was hurt.", 7, "reg 7"),
      s("The occurrence you chose is listed in Schedule 2 at the paragraph shown next to it.", 2, "Sch 2"),
    ],
  },

  // ---- Who reports ----------------------------------------------------------
  "who-employer": {
    id: "who-employer",
    heading: "Who reports it: you, as the employer",
    lines: [r("For an employee, the employer is the responsible person who must report it.", 3, "reg 3(1)(a)(i)")],
  },
  "who-other-employer": {
    id: "who-other-employer",
    heading: "Who reports it: their employer",
    lines: [
      r("For an employee, their own employer is the responsible person who must report it.", 3, "reg 3(1)(a)(i)"),
      tip("Tell their employer straight away, so they can report it in time."),
    ],
  },
  "who-you-control": {
    id: "who-you-control",
    heading: "Who reports it: you, as the person in control of the premises",
    lines: [
      r(
        "For a self-employed person, someone not at work, or a dangerous occurrence, the responsible person is whoever, through their business, was in control of the premises where it happened.",
        3,
        "reg 3(1)(a)(ii)",
      ),
    ],
  },
  "who-self-own": {
    id: "who-self-own",
    heading: "Who reports it: you",
    lines: [
      r("You were in control of the premises, so you are the responsible person.", 3, "reg 3(1)(a)(ii)"),
      h("HSE says that if you are self-employed and have a reportable accident on your own premises or in domestic premises, you report it.", HSE_WHO_SHOULD_REPORT_URL),
    ],
  },
  "who-self-other": {
    id: "who-self-other",
    heading: "Who reports it: the business in control of the site",
    lines: [
      r("For a self-employed person, the responsible person is whoever was in control of the premises where it happened.", 3, "reg 3(1)(a)(ii)"),
      h(
        "HSE says that if you are self-employed and have a specified injury or an over-7-day injury on someone else's premises, the person in control reports it, so you should make sure they know about it.",
        HSE_WHO_SHOULD_REPORT_URL,
      ),
    ],
  },
  "who-self-themselves": {
    id: "who-self-themselves",
    heading: "Who reports it: the self-employed worker",
    lines: [
      r("They were in control of the premises where it happened, so they are the responsible person who must report it.", 3, "reg 3(1)(a)(ii)"),
      tip("Make sure they know it is reportable, so they can report it in time."),
    ],
  },
  "who-other-control": {
    id: "who-other-control",
    heading: "Who reports it: the business in control of the site",
    lines: [
      r(
        "For a self-employed person, someone not at work, or a dangerous occurrence, the responsible person is whoever, through their business, was in control of the premises where it happened.",
        3,
        "reg 3(1)(a)(ii)",
      ),
      tip("Tell them straight away, so they can report it in time."),
    ],
  },
  "who-control-unsure": {
    id: "who-control-unsure",
    heading: "Who reports it: find out who was in control",
    lines: [
      r(
        "The report is made by whoever, through their business, was in control of the premises where it happened, at the time it happened.",
        3,
        "reg 3(1)(a)(ii)",
      ),
      tip("Agree with the other businesses on the site who will send the report, so it is not missed or late."),
    ],
  },
  "who-disease-employer": {
    id: "who-disease-employer",
    heading: "Who reports it: you, as the employer",
    lines: [r("For an employee's diagnosis, the employer is the responsible person who must report it.", 3, "reg 3(1)(b)(i)")],
  },
  "who-disease-self": {
    id: "who-disease-self",
    heading: "Who reports it: you",
    lines: [
      r("A relevant self-employed person reports their own diagnosis.", 3, "reg 3(1)(b)(ii) and 3(3)"),
      {
        text: "Construction work is one of the prescribed undertakings, so this covers self-employed people doing construction work.",
        cite: "Prescribed Undertakings Regulations 2015, Schedule para 3",
        url: PRESCRIBED_UNDERTAKINGS_URL,
      },
      s("You can arrange for someone else to send the report for you.", 1, "Sch 1 para 2(2)"),
    ],
  },
  "who-disease-other-employer": {
    id: "who-disease-other-employer",
    heading: "Who reports it: their employer",
    lines: [r("For an employee's diagnosis, their own employer is the responsible person who must report it.", 3, "reg 3(1)(b)(i)")],
  },
  "who-disease-other-self": {
    id: "who-disease-other-self",
    heading: "Who reports it: the self-employed worker",
    lines: [r("A relevant self-employed person reports their own diagnosis.", 3, "reg 3(1)(b)(ii) and 3(3)")],
  },

  // ---- How and when ---------------------------------------------------------
  "how-notify-phone": {
    id: "how-notify-phone",
    heading: "How and when to report",
    lines: [s("Tell HSE straight away, without delay.", 1, "Sch 1 para 1(1)(a)"), TEN_DAYS, PHONE_LINE],
  },
  "how-notify-online": {
    id: "how-notify-online",
    heading: "How and when to report",
    lines: [
      s("Tell HSE straight away, without delay.", 1, "Sch 1 para 1(1)(a)"),
      TEN_DAYS,
      h("Report online using HSE's RIDDOR forms. Phone reporting is only for deaths and specified injuries to workers.", HSE_HOW_TO_REPORT_URL),
    ],
  },
  "how-notify-self-own": {
    id: "how-notify-self-own",
    heading: "How and when to report",
    lines: [
      s(
        "A self-employed person injured at premises they own or occupy does not have to tell HSE straight away, but the report must still be sent within 10 days of the incident.",
        1,
        "Sch 1 para 1(2)",
      ),
      s("They can arrange for someone else to send the report for them.", 1, "Sch 1 para 1(2)"),
      s(
        "At other premises they control, such as a job in someone's home, tell HSE straight away, without delay, then send the report within 10 days of the incident.",
        1,
        "Sch 1 para 1(1)",
      ),
      PHONE_LINE,
    ],
  },
  "how-over7": {
    id: "how-over7",
    heading: "How and when to report",
    lines: [
      r("Send the report as soon as practicable, and in any event within 15 days of the accident.", 4, "reg 4(2)"),
      h("Report online using HSE's Report of an injury form.", HSE_HOW_TO_REPORT_URL),
    ],
  },
  "how-disease": {
    id: "how-disease",
    heading: "How and when to report",
    lines: [
      s("Send the report of the diagnosis without delay.", 1, "Sch 1 para 2(1)"),
      h("Report online using HSE's Report of a case of disease form.", HSE_HOW_TO_REPORT_URL),
    ],
  },
  "how-cancer-bio": {
    id: "how-cancer-bio",
    heading: "How to report",
    lines: [
      s("Notify HSE in the approved manner.", 1, "Sch 1 para 3"),
      h("Report online using HSE's Report of a case of disease form.", HSE_HOW_TO_REPORT_URL),
    ],
  },
  "keep-record": {
    id: "keep-record",
    heading: "Keep a record",
    lines: [
      r("Keep a record of every reportable incident and reportable diagnosis.", 12, "reg 12(1)"),
      r("Keep each entry for at least three years, at the place where the work is done or at your usual place of business.", 12, "reg 12(2)"),
    ],
  },

  // ---- Not reportable -------------------------------------------------------
  "not-work-related": {
    id: "not-work-related",
    heading: "Not reportable: RIDDOR covers work-related accidents only",
    lines: [r("RIDDOR reporting is for accidents arising out of or in connection with work.", 2, "reg 2(1) and (2)")],
  },
  "not-road": {
    id: "not-road",
    heading: "Not reportable: a road traffic accident",
    lines: [
      r(
        "Injuries and deaths arising from the movement of a vehicle on a road are not reportable under RIDDOR, unless loading or unloading, work on or alongside the road, a substance the vehicle was carrying, or a train was involved.",
        14,
        "reg 14(3)",
      ),
    ],
  },
  "not-over3": {
    id: "not-over3",
    heading: "Not reportable, but it must be recorded",
    lines: [
      r(
        "An injury that stops a person at work doing their normal work for more than 3 days in a row, not counting the day of the accident, must be recorded but not reported.",
        12,
        "reg 12(1)(c)",
      ),
      s(
        "Record the date and time of the accident, the person's full name, job and injury, where it happened, and a short description of what happened.",
        1,
        "Sch 1 paras 18 to 21",
      ),
      r("Keep the record for at least three years.", 12, "reg 12(2)"),
      r("If it goes on to more than 7 days in a row, it becomes reportable: report it within 15 days of the accident.", 4, "reg 4(2)"),
    ],
  },
  "not-minor": {
    id: "not-minor",
    heading: "Not reportable under RIDDOR",
    lines: [
      r("An injury to a worker is reportable only if it is a specified injury or it stops them doing their normal work for more than 7 days in a row.", 4, "reg 4"),
      r("If it goes on to more than 3 days in a row, record it. If it goes on to more than 7, report it within 15 days of the accident.", 12, "reg 12(1)(c) and reg 4(2)"),
    ],
  },
  "not-public": {
    id: "not-public",
    heading: "Not reportable under RIDDOR",
    lines: [
      r(
        "An injury to someone not at work is reportable only if they are taken from the scene to hospital for treatment of the injury, or, for an accident on hospital premises, if it is a specified injury.",
        5,
        "reg 5",
      ),
      h(
        "HSE says examinations and diagnostic tests, such as X-rays, do not count as treatment, and there is no need to report people taken to hospital purely as a precaution when no injury is apparent.",
        HSE_REPORTABLE_INCIDENTS_URL,
      ),
      tip("Write down what happened anyway, while the details are fresh."),
    ],
  },
  "not-disease": {
    id: "not-disease",
    heading: "Not reportable under RIDDOR",
    lines: [
      r(
        "Apart from offshore workplaces, which have their own list in regulation 10, only the conditions listed in regulations 8 and 9 are reportable, and those in regulation 8 only where the person's work involves the activity linked to them.",
        8,
        "regs 8 and 9",
      ),
      tip("If a doctor later diagnoses one of the listed conditions, run the checker again."),
    ],
  },
  "not-death-self-control": {
    id: "not-death-self-control",
    heading: "Not reportable under RIDDOR: a self-employed person's death on premises they controlled",
    lines: [
      r(
        "The duty to report a work-related death does not apply to a self-employed person who suffers a fatal accident on premises controlled by that self-employed person.",
        6,
        "reg 6(4)",
      ),
      h(
        "This is only about reporting under RIDDOR. HSE's work-related deaths protocol says the police and the other organisations that sign it have roles in investigating or prosecuting work-related deaths, and that coroners are closely involved.",
        HSE_WORK_RELATED_DEATHS_URL,
      ),
      tip("If the emergency services have not been called, call 999."),
      tip("If anyone else was hurt, or something dangerous also happened, check that separately. It may still be reportable."),
    ],
  },
  "accident-book": {
    id: "accident-book",
    heading: "Record it in your accident book",
    lines: [
      {
        text: "If you normally employ 10 or more people at the same time on or about the same premises, you must keep an accident book (on paper or electronic) where an injured employee can record an accident, and keep each entry for at least 3 years.",
        cite: "Social Security (Claims and Payments) Regulations 1979, reg 25(3)",
        url: SS_CLAIMS_REG_25_URL,
      },
      tip("Even with fewer people, writing down what happened helps if questions are asked later."),
    ],
  },

  // ---- Get advice -----------------------------------------------------------
  "advice-work": {
    id: "advice-work",
    heading: "Check whether it was connected with work",
    lines: [
      r(
        "A work-related accident is one arising out of or in connection with work. That includes one caused by how the work was done, the plant or substances used, or the condition of the premises.",
        2,
        "reg 2(1) and (2)",
      ),
      h("HSE's guidance on reportable incidents explains what counts.", HSE_REPORTABLE_INCIDENTS_URL),
      s("If it was work-related and is reportable, the deadline runs from the date of the incident, so do not wait to find out.", 1, "Sch 1 para 1(1)"),
    ],
  },
  "advice-specified": {
    id: "advice-specified",
    heading: "Wait for the diagnosis, but watch the deadline",
    lines: [
      r("Some specified injuries depend on a doctor's diagnosis, such as a fracture or an eye injury likely to cause permanent blinding.", 4, "reg 4(1)"),
      s("If the diagnosis confirms a specified injury, tell HSE without delay and send the report within 10 days of the incident.", 1, "Sch 1 para 1(1)"),
      r("If it is not a specified injury, it is still reportable if the worker cannot do their normal work for more than 7 days in a row: report it within 15 days of the accident.", 4, "reg 4(2)"),
    ],
  },
  "advice-days": {
    id: "advice-days",
    heading: "Keep counting the days",
    lines: [
      r("Count the days after the accident, including weekends and rest days, but not the day of the accident.", 2, "reg 2(1) and reg 4(2)"),
      r("More than 3 days in a row: record it.", 12, "reg 12(1)(c)"),
      r("More than 7 days in a row: report it within 15 days of the accident.", 4, "reg 4(2)"),
      h("HSE says that where an injury only becomes apparent later, report it as soon as it has stopped the worker doing their normal work for more than 7 days in a row.", HSE_REPORTABLE_INCIDENTS_URL),
    ],
  },
  "advice-hospital": {
    id: "advice-hospital",
    heading: "Find out whether they were treated",
    lines: [
      r("It is reportable if they were taken from the scene to hospital for treatment of the injury.", 5, "reg 5(a)"),
      h("HSE says a dressing, stitches, a plaster cast or surgery count as treatment. Examinations and tests, such as X-rays, do not.", HSE_REPORTABLE_INCIDENTS_URL),
      s("If it is reportable, tell HSE without delay and send the report within 10 days of the incident.", 1, "Sch 1 para 1(1)"),
    ],
  },
  "advice-disease": {
    id: "advice-disease",
    heading: "Ask the doctor about the diagnosis",
    lines: [
      r("Ask whether the diagnosis is one of the conditions listed in regulation 8 or 9, and whether it is linked to the person's work.", 8, "regs 8 and 9"),
      h("HSE's guidance on occupational diseases explains each one.", HSE_OCCUPATIONAL_DISEASES_URL),
      s("If it is reportable, send the report without delay.", 1, "Sch 1 para 2(1)"),
    ],
  },
  "advice-hospital-premises": {
    id: "advice-hospital-premises",
    heading: "Check whether it was a specified injury",
    lines: [
      r(
        "For someone not at work, an accident on hospital premises is reportable if the injury is one of the specified injuries listed in regulation 4(1), such as a bone fracture other than to a finger, thumb or toe.",
        5,
        "reg 5(b)",
      ),
      r("The report is made by whoever, through their business, was in control of the premises where it happened.", 3, "reg 3(1)(a)(ii)"),
      s("If it is reportable, tell HSE without delay and send the report within 10 days of the incident.", 1, "Sch 1 para 1(1)"),
    ],
  },
  "advice-occurrence": {
    id: "advice-occurrence",
    heading: "Check the full list of dangerous occurrences",
    lines: [
      s("Read the full list in Schedule 2 and check the exact wording of the one that looks closest.", 2, "Sch 2"),
      h("HSE's guidance on dangerous occurrences explains each one.", HSE_DANGEROUS_OCCURRENCES_URL),
      s("If it is listed, tell HSE without delay and send the report within 10 days of the incident.", 1, "Sch 1 para 1(1)"),
    ],
  },
  "advice-occurrence-unlisted": {
    id: "advice-occurrence-unlisted",
    heading: "Check the full list before you decide",
    lines: [
      s(
        "The list in this checker is only the dangerous occurrences most likely on a building site. Schedule 2 lists more, for example explosives (paragraph 5), biological agents (paragraph 10), and damage to a pipeline, or a change in its position that needs immediate attention (paragraphs 21 and 22).",
        2,
        "Sch 2",
      ),
      h("HSE's guidance on dangerous occurrences explains each one.", HSE_DANGEROUS_OCCURRENCES_URL),
      s("If it is listed, tell HSE without delay and send the report within 10 days of the incident.", 1, "Sch 1 para 1(1)"),
      tip("If it is not listed, record it as a near miss and find out why it happened, so it does not happen again."),
    ],
  },
};

// ---------------------------------------------------------------------------
// Verdicts
// ---------------------------------------------------------------------------

export type Verdict = "reportable" | "record" | "not-reportable" | "advice";

export const VERDICT_HEADLINES: Readonly<Record<Verdict, string>> = {
  reportable: "Reportable under RIDDOR",
  record: "Not reportable under RIDDOR, but record it",
  "not-reportable": "Not reportable under RIDDOR",
  advice: "Get advice before you decide",
};

export const ADVICE_FOOTER =
  "If you are still not sure, ask a competent health and safety adviser. Do not let the deadline pass while you find out.";

export interface RiddorResult {
  verdict: Verdict;
  blocks: BlockId[];
}

export interface Assessment {
  /** The questions to show for the answers so far, in order. */
  questions: Question[];
  /** The outcome, once every question that applies has an answer. */
  result: RiddorResult | null;
}

const WORKERS: readonly Person[] = ["employee", "other-employee", "me", "self-employed"];

/**
 * Walks the decision tree for the answers given. Answers to questions that do
 * not apply to the path are ignored, so stale state from a changed answer can
 * never change the outcome.
 */
export function assess(answers: RiddorAnswers): Assessment {
  const questions: Question[] = [Q_WHAT];
  const done = (verdict: Verdict, blocks: BlockId[]): Assessment => ({ questions, result: { verdict, blocks } });
  const pending = (): Assessment => ({ questions, result: null });

  /**
   * Who reports an injury, death or dangerous occurrence; null while the
   * control question is unanswered. "them" (the self-employed person
   * themselves) is only offered, and only accepted, for a self-employed person
   * who is not the visitor.
   */
  const whoReports = (person: Person | "none", what: WhatHappened): BlockId | null => {
    if (person === "employee") return "who-employer";
    if (person === "other-employee") return "who-other-employer";
    const offerThem = person === "self-employed" && (what === "death" || what === "injury");
    questions.push(offerThem ? controlQuestionForSelfEmployed(what) : Q_CONTROL);
    if (answers.control === "me") return person === "me" ? "who-self-own" : "who-you-control";
    if (answers.control === "them" && offerThem) return "who-self-themselves";
    if (answers.control === "other") return person === "me" ? "who-self-other" : "who-other-control";
    if (answers.control === "unsure") return "who-control-unsure";
    return null;
  };

  const what = answers.what;
  if (!what) return pending();

  if (what === "death" || what === "injury") {
    questions.push(Q_WORK_RELATED);
    if (!answers.workRelated) return pending();
    if (answers.workRelated === "no") return done("not-reportable", ["not-work-related"]);
    if (answers.workRelated === "unsure") return done("advice", ["advice-work"]);

    questions.push(Q_ROAD);
    if (!answers.road) return pending();
    if (answers.road === "ordinary") return done("record", ["not-road", "accident-book"]);

    const personQ = personQuestion(what);
    questions.push(personQ);
    const person = answers.person;
    if (!person || !personQ.options.some((option) => option.value === person)) return pending();

    if (what === "death") {
      const who = whoReports(person, what);
      if (!who) return pending();
      // reg 6(4): a self-employed person's fatal accident on premises they controlled is not reportable.
      if (who === "who-self-themselves") return done("not-reportable", ["not-death-self-control"]);
      return done("reportable", ["death", who, "how-notify-phone", "keep-record"]);
    }

    if (person === "public") {
      questions.push(Q_HOSPITAL);
      if (!answers.hospital) return pending();
      if (answers.hospital === "checks-only" || answers.hospital === "no") return done("record", ["not-public"]);
      if (answers.hospital === "unsure") return done("advice", ["advice-hospital"]);
      // reg 5(b): on hospital premises the test is a specified injury, not being taken to hospital.
      if (answers.hospital === "hospital-premises") return done("advice", ["advice-hospital-premises"]);
      const who = whoReports(person, what);
      if (!who) return pending();
      return done("reportable", ["public-hospital", who, "how-notify-online", "keep-record"]);
    }

    if (!WORKERS.includes(person)) return pending();
    questions.push(Q_SPECIFIED);
    if (!answers.specified) return pending();
    if (answers.specified === "unsure") return done("advice", ["advice-specified"]);
    if (answers.specified === "yes") {
      const who = whoReports(person, what);
      if (!who) return pending();
      // Sch 1 para 1(2): a self-employed person injured at premises they own or occupy need not notify straight away.
      const selfOwn = who === "who-self-own" || who === "who-self-themselves";
      return done("reportable", ["specified", who, selfOwn ? "how-notify-self-own" : "how-notify-phone", "keep-record"]);
    }

    questions.push(Q_DAYS);
    if (!answers.days) return pending();
    if (answers.days === "unsure") return done("advice", ["advice-days"]);
    if (answers.days === "over3") return done("record", ["not-over3", "accident-book"]);
    if (answers.days === "three-or-fewer") return done("record", ["not-minor", "accident-book"]);
    const who = whoReports(person, what);
    if (!who) return pending();
    return done("reportable", ["over7", who, "how-over7", "keep-record"]);
  }

  if (what === "disease") {
    questions.push(Q_DISEASE);
    const disease = answers.disease;
    if (!disease) return pending();
    if (disease === "none") return done("not-reportable", ["not-disease"]);
    if (disease === "unsure") return done("advice", ["advice-disease"]);
    questions.push(Q_DIAGNOSIS_PERSON);
    if (!answers.diagnosisPerson) return pending();
    const who: BlockId = {
      employee: "who-disease-employer",
      me: "who-disease-self",
      "other-employee": "who-disease-other-employer",
      "self-employed": "who-disease-other-self",
    }[answers.diagnosisPerson] as BlockId;
    return disease === "cancer-bio"
      ? done("reportable", ["cancer-bio", who, "how-cancer-bio", "keep-record"])
      : done("reportable", ["disease", who, "how-disease", "keep-record"]);
  }

  // what === "occurrence"
  questions.push(Q_OCCURRENCE);
  const occurrence = answers.occurrence;
  if (!occurrence) return pending();
  // The list is a construction shortlist, not all of Schedule 2, so "None of these" is never a confident negative.
  if (occurrence === "none") return done("advice", ["advice-occurrence-unlisted"]);
  if (occurrence === "unsure") return done("advice", ["advice-occurrence"]);
  const who = whoReports("none", what);
  if (!who) return pending();
  return done("reportable", ["occurrence", who, "how-notify-online", "keep-record"]);
}

/**
 * The answers with every answer that no longer applies removed, so changing
 * an earlier answer clears the follow-ups that depended on it.
 */
export function pruneAnswers(answers: RiddorAnswers): RiddorAnswers {
  const asked = new Set(assess(answers).questions.map((question) => question.id));
  const kept: RiddorAnswers = {};
  for (const [key, value] of Object.entries(answers) as [QuestionId, string][]) {
    if (asked.has(key)) (kept as Record<string, string>)[key] = value;
  }
  return kept;
}

/** Shown under every result and on the page. */
export const SCOPE_LINE =
  "This covers Great Britain under RIDDOR 2013. Northern Ireland has its own RIDDOR (Northern Ireland) 1997 regulations, and reports there go to HSENI.";
export const NOT_LEGAL_ADVICE = "This is general guidance, not legal advice.";

// ---------------------------------------------------------------------------
// Summary table shown on the page ("What is reportable, and by when")
// ---------------------------------------------------------------------------

export interface SummaryRow {
  event: string;
  when: string;
  deadline: string;
  cite: string;
  url: string;
}

export const SUMMARY_ROWS: readonly SummaryRow[] = [
  {
    event: "A death",
    when: "Anyone who dies as a result of a work-related accident.",
    deadline: "Tell HSE without delay. Report within 10 days.",
    cite: "reg 6 and Sch 1 para 1",
    url: riddorRegulationUrl(6),
  },
  {
    event: "A specified injury to a worker",
    when: "One of the injuries listed in regulation 4(1), such as a fracture other than to a finger, thumb or toe.",
    deadline: "Tell HSE without delay. Report within 10 days.",
    cite: "reg 4(1) and Sch 1 para 1",
    url: riddorRegulationUrl(4),
  },
  {
    event: "An over-7-day injury to a worker",
    when: "The worker cannot do their normal work for more than 7 days in a row, not counting the day of the accident.",
    deadline: "Report within 15 days of the accident.",
    cite: "reg 4(2)",
    url: riddorRegulationUrl(4),
  },
  {
    event: "An injury to someone not at work",
    when: "They are taken from the scene to hospital for treatment of the injury.",
    deadline: "Tell HSE without delay. Report within 10 days.",
    cite: "reg 5 and Sch 1 para 1",
    url: riddorRegulationUrl(5),
  },
  {
    event: "An occupational disease",
    when: "A doctor diagnoses one of the conditions in regulation 8, such as HAVS or occupational dermatitis, and the work involves the linked activity.",
    deadline: "Report without delay once you have the diagnosis.",
    cite: "reg 8 and Sch 1 para 2",
    url: riddorRegulationUrl(8),
  },
  {
    event: "A dangerous occurrence",
    when: "One of the events listed in Schedule 2, such as a scaffold collapse, even if nobody was hurt.",
    deadline: "Tell HSE without delay. Report within 10 days.",
    cite: "reg 7 and Sch 2",
    url: riddorScheduleUrl(2),
  },
  {
    event: "An over-3-day injury to a worker",
    when: "The worker cannot do their normal work for more than 3 days in a row, but not more than 7.",
    deadline: "Not reported. Record it and keep the record for three years.",
    cite: "reg 12(1)(c) and (2)",
    url: riddorRegulationUrl(12),
  },
];
